import React, { useState, useEffect, useRef } from 'react'
import {
  Container,
  Table,
  Breadcrumb,
  Button,
  InputGroup,
  FormControl,
  Modal,
  Badge,
  Form,
  Card,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { Link, useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import toast from 'react-hot-toast'
import useGetSandboxes from 'hooks/use-get-sandboxes'
import useGetSandboxStats from 'hooks/use-get-sandbox-stats'
import useSandboxMessageCounts from 'hooks/use-sandbox-message-counts'
import {
  useApproveSandbox,
  useRejectSandbox,
  useRevokeSandbox,
  useReactivateSandbox,
  useReactivateSandboxWithPhone,
  useUpdateSandboxExpiry,
  useCreateDirectSandbox,
  useForceActivateSandbox,
} from 'hooks/use-sandbox-actions'
import { Sandbox, SandboxStatus, SandboxPagination } from 'interfaces/sandbox'
import useDebounce from 'hooks/use-debounce'

const STATUS_OPTIONS: { value: SandboxStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'email_verified', label: 'Email Verified' },
  { value: 'phone_verified', label: 'Phone Verified' },
  { value: 'filtered', label: 'Filtered' },
  { value: 'routed_to_sales', label: 'Routed To Sales' },
  { value: 'queued', label: 'Queued' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
]

const getStatusBadgeVariant = (status: SandboxStatus): string => {
  switch (status) {
    case 'active':
      return 'success'
    case 'pending':
    case 'email_verified':
    case 'phone_verified':
    case 'filtered':
    case 'queued':
      return 'warning'
    case 'routed_to_sales':
      return 'info'
    case 'expired':
      return 'secondary'
    case 'rejected':
    case 'revoked':
      return 'danger'
    default:
      return 'light'
  }
}

const formatStatus = (status: string): string => {
  if (status === 'pending') {
    return 'OTP Verification Pending'
  }
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateShort = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

const getLeadScoreColor = (score: number | null): string => {
  if (score === null) return '#6c757d'
  if (score >= 80) return '#28a745'
  if (score >= 60) return '#5cb85c'
  if (score >= 40) return '#ffc107'
  return '#dc3545'
}

const getConfidenceBadge = (
  confidence: string | null
): { variant: string; label: string } => {
  switch (confidence) {
    case 'high':
      return { variant: 'success', label: 'High' }
    case 'medium':
      return { variant: 'warning', label: 'Med' }
    case 'low':
      return { variant: 'danger', label: 'Low' }
    default:
      return { variant: 'secondary', label: '-' }
  }
}

const getDaysRemaining = (trialEndsAt: string | null): number | null => {
  if (!trialEndsAt) return null
  const now = new Date()
  const end = new Date(trialEndsAt)
  const diffTime = end.getTime() - now.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

// Lead Score Ring Component
const LeadScoreRing = ({
  score,
  size = 40,
}: {
  score: number | null
  size?: number
}) => {
  const displayScore = score ?? 0
  const circumference = 2 * Math.PI * 15
  const strokeDashoffset = circumference - (displayScore / 100) * circumference
  const color = getLeadScoreColor(score)

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`score-tooltip-${displayScore}`}>
          Lead Score: {score ?? 'N/A'}
        </Tooltip>
      }
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx="20"
          cy="20"
          r="15"
          fill="none"
          stroke="#2d2d2d"
          strokeWidth="4"
        />
        <circle
          cx="20"
          cy="20"
          r="15"
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={score === null ? circumference : strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
        <text
          x="20"
          y="20"
          textAnchor="middle"
          dominantBaseline="central"
          fill={color}
          fontSize="10"
          fontWeight="bold"
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
        >
          {score ?? '-'}
        </text>
      </svg>
    </OverlayTrigger>
  )
}

// Stats Card Component
const StatsCard = ({
  title,
  value,
  subtitle,
  variant = 'primary',
}: {
  title: string
  value: string | number
  subtitle?: string
  variant?: string
}) => (
  <Card className="h-100 bg-dark" style={{ border: '1px solid #444' }}>
    <Card.Body className="py-3">
      <div className="text-muted small text-uppercase">{title}</div>
      <div className={`h3 mb-0 text-${variant}`}>{value}</div>
      {subtitle && <div className="text-muted small">{subtitle}</div>}
    </Card.Body>
  </Card>
)

// Message counts cell - separate component so we can use hooks per row
const MessageCountsCell = ({ userId }: { userId: string | null }) => {
  const { data, isLoading } = useSandboxMessageCounts(userId)

  if (!userId) return <span className="text-muted">-</span>

  if (isLoading) {
    return (
      <span className="text-muted" style={{ fontSize: '0.85rem' }}>
        ...
      </span>
    )
  }

  if (!data) return <span className="text-muted">-</span>

  return (
    <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
      <div>{data.total_inbound} in</div>
      <div>{data.total_outbound} out</div>
    </div>
  )
}

const SandboxesPage = () => {
  const history = useHistory()
  const location = useLocation()
  const params = queryString.parse(location.search)

  const [searchQuery, setSearchQuery] = useState(
    (params.search as string) || ''
  )
  const [statusFilter, setStatusFilter] = useState<SandboxStatus | ''>(
    (params.status as SandboxStatus) || ''
  )
  const [page, setPage] = useState(1)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Modal state
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [selectedSandbox, setSelectedSandbox] = useState<Sandbox | null>(null)

  // Expiry edit modal state
  const [showExpiryModal, setShowExpiryModal] = useState(false)
  const [newExpiryDate, setNewExpiryDate] = useState('')

  // Reactivate-with-phone modal state (for expired sandboxes that still have a phone)
  const [showReactivateWithPhoneModal, setShowReactivateWithPhoneModal] =
    useState(false)
  const [reactivateExpiresAt, setReactivateExpiresAt] = useState('')

  // Create sandbox modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createEmail, setCreateEmail] = useState('')
  const [createCompanyName, setCreateCompanyName] = useState('')
  const [createPhoneNumber, setCreatePhoneNumber] = useState('')

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const {
    data: response,
    isLoading,
    error,
    isPreviousData,
    refetch,
    isFetching,
  } = useGetSandboxes({
    page,
    status: statusFilter,
    searchQuery: debouncedSearchQuery,
  })

  const { data: statsResponse } = useGetSandboxStats()

  const approveMutation = useApproveSandbox()
  const rejectMutation = useRejectSandbox()
  const revokeMutation = useRevokeSandbox()
  const reactivateMutation = useReactivateSandbox()
  const reactivateWithPhoneMutation = useReactivateSandboxWithPhone()
  const updateExpiryMutation = useUpdateSandboxExpiry()
  const createDirectMutation = useCreateDirectSandbox()
  const forceActivateMutation = useForceActivateSandbox()

  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchQuery, statusFilter])

  useEffect(() => {
    const currentParams = queryString.parse(location.search)
    const newParams = { ...currentParams }

    if (searchQuery) {
      newParams.search = searchQuery
    } else {
      delete newParams.search
    }

    if (statusFilter) {
      newParams.status = statusFilter
    } else {
      delete newParams.status
    }

    const newSearch = queryString.stringify(newParams)
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`

    if (location.search !== (newSearch ? `?${newSearch}` : '')) {
      history.replace(newUrl)
    }
  }, [searchQuery, statusFilter, history, location.pathname, location.search])

  const sandboxes: Sandbox[] = response?.sandboxes || []
  const pagination: SandboxPagination | undefined = response?.pagination

  const stats = statsResponse?.stats

  const handleApprove = async (sandbox: Sandbox) => {
    if (
      !window.confirm(
        `Approve this sandbox and provision the user?\n\nPlease verify the email: ${sandbox.email}`
      )
    )
      return

    try {
      await approveMutation.mutateAsync({
        sandboxId: sandbox.id,
        assignPhone: true,
      })
      toast.success('Sandbox approved and provisioned')
    } catch (err) {
      toast.error((err as Error).message || 'Failed to approve sandbox')
    }
  }

  const handleForceActivate = async (sandbox: Sandbox) => {
    if (
      !window.confirm(
        `Approve Anyway — this will bypass OTP and activate the sandbox for:\n\n${sandbox.email}\n\nContinue?`
      )
    )
      return

    try {
      await forceActivateMutation.mutateAsync({ sandboxId: sandbox.id })
      toast.success('Sandbox force-activated successfully')
    } catch (err) {
      toast.error((err as Error).message || 'Failed to force activate sandbox')
    }
  }

  const handleRejectClick = (sandbox: Sandbox) => {
    setSelectedSandbox(sandbox)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleRejectConfirm = async () => {
    if (!selectedSandbox || !rejectReason.trim()) return

    try {
      await rejectMutation.mutateAsync({
        sandboxId: selectedSandbox.id,
        reason: rejectReason,
      })
      toast.success('Sandbox rejected')
      setShowRejectModal(false)
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reject sandbox')
    }
  }

  const handleReactivate = async (sandbox: Sandbox) => {
    if (
      !window.confirm(
        `Reactivate this expired sandbox and assign a new phone?\n\nEmail: ${sandbox.email}`
      )
    )
      return

    try {
      await reactivateMutation.mutateAsync({ sandboxId: sandbox.id })
      toast.success('Sandbox reactivated')
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reactivate sandbox')
    }
  }

  const handleReactivateWithPhoneClick = (sandbox: Sandbox) => {
    setSelectedSandbox(sandbox)
    setReactivateExpiresAt('')
    setShowReactivateWithPhoneModal(true)
  }

  const handleReactivateWithPhoneConfirm = async () => {
    if (!selectedSandbox || !reactivateExpiresAt) return

    try {
      await reactivateWithPhoneMutation.mutateAsync({
        sandboxId: selectedSandbox.id,
        expiresAt: new Date(`${reactivateExpiresAt}T23:59:59`).toISOString(),
      })
      toast.success('Sandbox reactivated with existing phone')
      setShowReactivateWithPhoneModal(false)
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reactivate sandbox')
    }
  }

  const handleRevoke = async (sandbox: Sandbox) => {
    if (
      !window.confirm(
        `Revoke this sandbox? The phone will be returned to the pool.\n\nPlease verify the email: ${sandbox.email}`
      )
    )
      return

    try {
      await revokeMutation.mutateAsync({ sandboxId: sandbox.id })
      toast.success('Sandbox revoked')
    } catch (err) {
      toast.error((err as Error).message || 'Failed to revoke sandbox')
    }
  }

  const handleEditExpiryClick = (sandbox: Sandbox) => {
    setSelectedSandbox(sandbox)
    // Convert ISO date to date format (YYYY-MM-DD)
    const expiryDate = sandbox.trial_ends_at
      ? new Date(sandbox.trial_ends_at).toISOString().slice(0, 10)
      : ''
    setNewExpiryDate(expiryDate)
    setShowExpiryModal(true)
  }

  const handleExpiryConfirm = async () => {
    if (!selectedSandbox || !newExpiryDate) return

    try {
      // Send just the date - backend sets end of day in sandbox's timezone
      await updateExpiryMutation.mutateAsync({
        sandboxId: selectedSandbox.id,
        expiresAt: newExpiryDate,
      })
      toast.success('Trial expiry updated')
      setShowExpiryModal(false)
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update expiry')
    }
  }

  const handleOpenCreateModal = () => {
    setCreateName('')
    setCreateEmail('')
    setCreateCompanyName('')
    setCreatePhoneNumber('')
    setShowCreateModal(true)
  }

  const handleCreateSandbox = async () => {
    if (
      !createName.trim() ||
      !createEmail.trim() ||
      !createCompanyName.trim()
    ) {
      toast.error('Name, email, and company name are required')
      return
    }

    try {
      const result = await createDirectMutation.mutateAsync({
        name: createName.trim(),
        email: createEmail.trim(),
        companyName: createCompanyName.trim(),
        phoneNumber: createPhoneNumber.trim() || undefined,
      })
      toast.success(result.message || 'Sandbox created successfully')
      setShowCreateModal(false)
    } catch (err) {
      toast.error((err as Error).message || 'Failed to create sandbox')
    }
  }

  const canEditExpiry = (sandbox: Sandbox): boolean => {
    return (
      sandbox.status === 'active' && sandbox.partner_integration_id !== null
    )
  }

  const canApprove = (sandbox: Sandbox): boolean => {
    return ['phone_verified', 'filtered', 'routed_to_sales'].includes(
      sandbox.status
    )
  }

  const canForceActivate = (sandbox: Sandbox): boolean => {
    return sandbox.status === 'pending'
  }

  const canReject = (sandbox: Sandbox): boolean => {
    // Don't allow rejecting queued sandboxes - they're already approved
    return [
      'pending',
      'email_verified',
      'phone_verified',
      'filtered',
      'routed_to_sales',
    ].includes(sandbox.status)
  }

  const canRevoke = (sandbox: Sandbox): boolean => {
    return sandbox.status === 'active'
  }

  const canReverseRejection = (sandbox: Sandbox): boolean => {
    return sandbox.status === 'rejected' && sandbox.user_id === null
  }

  const canReactivate = (sandbox: Sandbox): boolean => {
    return (
      sandbox.status === 'expired' &&
      !sandbox.assigned_phone_number &&
      sandbox.user_id !== null
    )
  }

  const canReactivateWithPhone = (sandbox: Sandbox): boolean => {
    return (
      sandbox.status === 'expired' &&
      !!sandbox.assigned_phone_number &&
      sandbox.user_id !== null
    )
  }

  if (error) {
    const errorMessage = (error as Error).message
    return <div>Error: {errorMessage}</div>
  }

  return (
    <Container className="mb-4 pt-4" fluid style={{ maxWidth: '1600px' }}>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active>Sandboxes</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <div>
          <h1 className="mb-1">Sandbox Signups</h1>
          <p className="text-muted mb-0">
            Signup filtering and trial management for Linq Blue
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ion-icon
              name="refresh-outline"
              style={{
                animation: isFetching ? 'spin 1s linear infinite' : 'none',
              }}
            />
          </Button>
          <Button variant="primary" onClick={handleOpenCreateModal}>
            + Create Sandbox
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <Row className="mb-4 g-3">
        <Col xs={6} md={3} lg={2}>
          <StatsCard title="Total" value={stats?.total ?? '-'} variant="info" />
        </Col>
        <Col xs={6} md={3} lg={2}>
          <StatsCard
            title="Active Trials"
            value={stats?.active ?? '-'}
            variant="success"
          />
        </Col>
        <Col xs={6} md={3} lg={2}>
          <StatsCard
            title="Pending Review"
            value={stats?.pending ?? '-'}
            variant="warning"
          />
        </Col>
        <Col xs={6} md={3} lg={2}>
          <StatsCard
            title="Routed to Sales"
            value={stats?.routed_to_sales ?? '-'}
            variant="info"
          />
        </Col>
        <Col xs={6} md={3} lg={2}>
          <StatsCard
            title="Auto-Provisioned"
            value={stats?.auto_provisioned ?? '-'}
            variant="success"
          />
        </Col>
        <Col xs={6} md={3} lg={2}>
          <StatsCard
            title="Avg Score"
            value={stats?.avg_score ?? '-'}
            variant={(stats?.avg_score ?? 0) >= 70 ? 'success' : 'warning'}
          />
        </Col>
      </Row>

      {/* Filters */}
      <div className="d-flex gap-3 mb-4">
        <InputGroup style={{ flex: 1 }}>
          <InputGroup.Text>
            <ion-icon name="search-outline" />
          </InputGroup.Text>
          <FormControl
            ref={searchInputRef}
            autoComplete="off"
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
            type="text"
            placeholder="Search by email, name, phone, or company..."
            aria-label="Search Sandboxes"
          />
          {searchQuery && (
            <Button
              variant="outline-secondary"
              onClick={() => setSearchQuery('')}
            >
              <ion-icon name="close-outline" />
            </Button>
          )}
        </InputGroup>
        <select
          className="form-control"
          style={{ maxWidth: '180px' }}
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as SandboxStatus | '')
          }
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading && sandboxes.length === 0 && <div>Loading...</div>}

      {!isLoading && sandboxes.length === 0 && (
        <div className="alert alert-info">No sandbox signups found.</div>
      )}

      {sandboxes.length > 0 && (
        <>
          {isFetching && (
            <div
              className="d-flex align-items-center gap-2 mb-2 text-muted"
              style={{ fontSize: '0.9rem' }}
            >
              <ion-icon
                name="refresh-outline"
                style={{ animation: 'spin 1s linear infinite' }}
              />
              Refreshing...
            </div>
          )}
          <div
            style={{
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <Table
              hover
              responsive
              variant="dark"
              className="mb-0"
              style={{ fontSize: '0.95rem' }}
            >
              <thead style={{ background: '#16213e' }}>
                <tr>
                  <th style={{ width: '50px' }}>Score</th>
                  <th style={{ width: '140px' }}>Decision</th>
                  <th>Signup</th>
                  <th style={{ width: '180px' }}>Contact</th>
                  <th style={{ width: '130px' }}>Status</th>
                  <th style={{ width: '130px' }}>Assigned #</th>
                  <th style={{ width: '80px' }}>Messages</th>
                  <th style={{ width: '100px' }}>Trial</th>
                  <th style={{ width: '160px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sandboxes.map((sandbox: Sandbox) => {
                  const daysRemaining = getDaysRemaining(sandbox.trial_ends_at)
                  const confidenceBadge = getConfidenceBadge(
                    sandbox.filter_confidence
                  )

                  return (
                    <tr key={sandbox.id}>
                      {/* Lead Score */}
                      <td className="align-middle text-center">
                        <LeadScoreRing score={sandbox.lead_score} size={36} />
                      </td>

                      {/* Decision + Confidence */}
                      <td className="align-middle">
                        <div className="d-flex flex-column gap-1">
                          {sandbox.filter_decision === 'auto_provision' ? (
                            <Badge
                              variant="success"
                              style={{
                                fontSize: '0.8rem',
                                padding: '4px 8px',
                              }}
                            >
                              AUTO
                            </Badge>
                          ) : sandbox.filter_decision === 'route_to_sales' ? (
                            <Badge
                              variant="info"
                              style={{
                                fontSize: '0.8rem',
                                padding: '4px 8px',
                              }}
                            >
                              SALES
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              style={{
                                fontSize: '0.8rem',
                                padding: '4px 8px',
                              }}
                            >
                              -
                            </Badge>
                          )}
                          <Badge
                            variant={confidenceBadge.variant}
                            style={{
                              fontSize: '0.75rem',
                              padding: '2px 6px',
                              opacity: 0.8,
                            }}
                          >
                            {confidenceBadge.label} conf
                          </Badge>
                        </div>
                      </td>

                      {/* Signup Info */}
                      <td className="align-middle">
                        <div>
                          <strong>{sandbox.name}</strong>
                          <span className="text-muted ms-2">#{sandbox.id}</span>
                          {sandbox.user_id && (
                            <Link
                              to={`/user/${sandbox.user_id}`}
                              className="ms-2"
                            >
                              <Button variant="outline-info" size="sm">
                                User
                              </Button>
                            </Link>
                          )}
                          {sandbox.organization_id && (
                            <Link
                              to={`/organizations/${sandbox.organization_id}`}
                              className="ms-1"
                            >
                              <Button variant="outline-light" size="sm">
                                Org
                              </Button>
                            </Link>
                          )}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {sandbox.company_name || 'No company'}
                        </div>
                        <div
                          className="d-flex gap-1 mt-1"
                          style={{ fontSize: '0.8rem' }}
                        >
                          {sandbox.email_verified && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`email-verified-${sandbox.id}`}>
                                  Email verified
                                </Tooltip>
                              }
                            >
                              <Badge
                                style={{
                                  border: '1px solid #28a745',
                                  color: '#28a745',
                                  background: 'transparent',
                                  cursor: 'default',
                                }}
                              >
                                Email
                              </Badge>
                            </OverlayTrigger>
                          )}
                          {sandbox.phone_verified && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`phone-verified-${sandbox.id}`}>
                                  Phone verified
                                </Tooltip>
                              }
                            >
                              <Badge
                                style={{
                                  border: '1px solid #28a745',
                                  color: '#28a745',
                                  background: 'transparent',
                                  cursor: 'default',
                                }}
                              >
                                Phone
                              </Badge>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="align-middle">
                        <div className="d-flex align-items-center gap-1">
                          <span
                            className="text-truncate"
                            style={{ maxWidth: '150px' }}
                          >
                            {sandbox.email}
                          </span>
                          <span
                            style={{
                              cursor: 'pointer',
                              opacity: 0.5,
                            }}
                            onClick={() => {
                              navigator.clipboard.writeText(sandbox.email)
                              toast.success('Copied')
                            }}
                            title="Copy email"
                          >
                            <ion-icon
                              name="copy-outline"
                              style={{ fontSize: '14px' }}
                            />
                          </span>
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {sandbox.phone_number}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="align-middle">
                        <Badge variant={getStatusBadgeVariant(sandbox.status)}>
                          {formatStatus(sandbox.status)}
                        </Badge>
                        {sandbox.filter_reason && (
                          <div
                            className="text-muted mt-1"
                            style={{ fontSize: '0.85rem' }}
                          >
                            {sandbox.filter_reason}
                          </div>
                        )}
                      </td>

                      {/* Assigned Phone */}
                      <td className="align-middle">
                        {sandbox.assigned_phone_number ? (
                          <span
                            className="text-success"
                            style={{ fontSize: '0.85rem' }}
                          >
                            {sandbox.assigned_phone_number}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      {/* Messages */}
                      <td className="align-middle">
                        <MessageCountsCell
                          userId={
                            sandbox.user_id ? String(sandbox.user_id) : null
                          }
                        />
                      </td>

                      {/* Trial */}
                      <td className="align-middle">
                        {sandbox.trial_ends_at ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`trial-tooltip-${sandbox.id}`}>
                                Expires: {formatDate(sandbox.trial_ends_at)}
                              </Tooltip>
                            }
                          >
                            <div>
                              {daysRemaining !== null && daysRemaining < 0 ? (
                                <Badge variant="danger">Expired</Badge>
                              ) : daysRemaining !== null &&
                                daysRemaining <= 2 ? (
                                <Badge variant="warning">
                                  {daysRemaining}d left
                                </Badge>
                              ) : (
                                <span style={{ fontSize: '0.85rem' }}>
                                  {formatDateShort(sandbox.trial_ends_at)}
                                </span>
                              )}
                            </div>
                          </OverlayTrigger>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                        <div
                          className="text-muted"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {formatDateShort(sandbox.created_at)}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="align-middle">
                        <div className="d-flex gap-1 flex-wrap">
                          {canApprove(sandbox) && (
                            <Button
                              variant="success"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleApprove(sandbox)}
                              disabled={approveMutation.isLoading}
                            >
                              Approve
                            </Button>
                          )}
                          {canForceActivate(sandbox) && (
                            <Button
                              variant="success"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleForceActivate(sandbox)}
                              disabled={forceActivateMutation.isLoading}
                            >
                              Approve Anyway
                            </Button>
                          )}
                          {canReject(sandbox) && (
                            <Button
                              variant="warning"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleRejectClick(sandbox)}
                              disabled={rejectMutation.isLoading}
                            >
                              Reject
                            </Button>
                          )}
                          {canRevoke(sandbox) && (
                            <Button
                              variant="danger"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleRevoke(sandbox)}
                              disabled={revokeMutation.isLoading}
                            >
                              Revoke
                            </Button>
                          )}
                          {canReverseRejection(sandbox) && (
                            <Button
                              variant="info"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleApprove(sandbox)}
                              disabled={approveMutation.isLoading}
                            >
                              Reverse Rejection & Approve
                            </Button>
                          )}
                          {canReactivate(sandbox) && (
                            <Button
                              variant="warning"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleReactivate(sandbox)}
                              disabled={reactivateMutation.isLoading}
                            >
                              Reactivate
                            </Button>
                          )}
                          {canReactivateWithPhone(sandbox) && (
                            <Button
                              variant="warning"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() =>
                                handleReactivateWithPhoneClick(sandbox)
                              }
                              disabled={reactivateWithPhoneMutation.isLoading}
                            >
                              Reactivate (keep #)
                            </Button>
                          )}
                          {canEditExpiry(sandbox) && (
                            <Button
                              variant="outline-light"
                              size="sm"
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.85rem',
                              }}
                              onClick={() => handleEditExpiryClick(sandbox)}
                            >
                              Expiry
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {pagination && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted">
            Page {pagination.current_page} of {pagination.total_pages} (
            {pagination.total_count} total)
          </span>
          <div>
            <Button
              variant="outline-light"
              onClick={() => setPage((old) => Math.max(old - 1, 1))}
              disabled={page === 1}
              className="me-2"
            >
              Previous
            </Button>
            <Button
              variant="outline-light"
              onClick={() => {
                if (
                  !isPreviousData &&
                  pagination.current_page < pagination.total_pages
                ) {
                  setPage((old) => old + 1)
                }
              }}
              disabled={
                isPreviousData ||
                pagination.current_page >= pagination.total_pages
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Sandbox</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSandbox && (
            <>
              <div className="alert alert-warning mb-3">
                <strong>Rejecting:</strong> {selectedSandbox.name}
                <br />
                <span className="text-muted">{selectedSandbox.email}</span>
              </div>
              {selectedSandbox.lead_score && (
                <div className="mb-3">
                  <strong>Lead Score:</strong> {selectedSandbox.lead_score}
                </div>
              )}
            </>
          )}
          <Form.Group>
            <Form.Label>Rejection Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleRejectConfirm}
            disabled={!rejectReason.trim() || rejectMutation.isLoading}
          >
            {rejectMutation.isLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Expiry Modal */}
      <Modal show={showExpiryModal} onHide={() => setShowExpiryModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Trial Expiry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSandbox && (
            <div className="alert alert-info mb-3">
              <strong>Sandbox:</strong> {selectedSandbox.name}
              <br />
              <span className="text-muted">{selectedSandbox.email}</span>
            </div>
          )}
          <Form.Group>
            <Form.Label>New Expiry Date</Form.Label>
            <Form.Control
              type="date"
              value={newExpiryDate}
              onChange={(e) => setNewExpiryDate(e.target.value)}
            />
            <Form.Text className="text-muted">
              Trial expires at end of day in user's timezone.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExpiryModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExpiryConfirm}
            disabled={!newExpiryDate || updateExpiryMutation.isLoading}
          >
            {updateExpiryMutation.isLoading ? 'Updating...' : 'Update Expiry'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Sandbox Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Sandbox User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info mb-3">
            <strong>Note:</strong> This creates a sandbox directly for a
            customer, bypassing the normal signup flow (no email verification
            required). International phone numbers (e.g. +44, +49, +61) are
            supported.
          </div>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="John Doe"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Company Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={createCompanyName}
              onChange={(e) => setCreateCompanyName(e.target.value)}
              placeholder="Acme Inc."
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Phone Number (optional)</Form.Label>
            <Form.Control
              type="tel"
              value={createPhoneNumber}
              onChange={(e) => setCreatePhoneNumber(e.target.value)}
              placeholder="+15551234567 or +447911123456"
            />
            <Form.Text className="text-muted">
              Customer's phone number. International numbers are accepted (e.g.
              +44, +49, +61). If not provided, a placeholder will be used.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateSandbox}
            disabled={
              !createName.trim() ||
              !createEmail.trim() ||
              !createCompanyName.trim() ||
              createDirectMutation.isLoading
            }
          >
            {createDirectMutation.isLoading ? 'Creating...' : 'Create Sandbox'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reactivate with Phone Modal — for expired sandboxes that still have an assigned phone */}
      <Modal
        show={showReactivateWithPhoneModal}
        onHide={() => setShowReactivateWithPhoneModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Reactivate Sandbox (Keep Phone)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSandbox && (
            <div className="alert alert-info mb-3">
              <strong>Sandbox:</strong> {selectedSandbox.name}
              <br />
              <span className="text-muted">{selectedSandbox.email}</span>
              <br />
              <span className="text-muted">
                Phone: {selectedSandbox.assigned_phone_number}
              </span>
            </div>
          )}
          <Form.Group>
            <Form.Label>New Expiry Date</Form.Label>
            <Form.Control
              type="date"
              value={reactivateExpiresAt}
              onChange={(e) => setReactivateExpiresAt(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
            />
            <Form.Text className="text-muted">
              The sandbox will be reactivated with the existing phone number
              until this date.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReactivateWithPhoneModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleReactivateWithPhoneConfirm}
            disabled={
              !reactivateExpiresAt || reactivateWithPhoneMutation.isLoading
            }
          >
            {reactivateWithPhoneMutation.isLoading
              ? 'Reactivating...'
              : 'Reactivate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default SandboxesPage
